import { ChevronDownIcon } from "@/components/icons";
import { Badge, Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { OrderItemDTO } from "@/modules/orders/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { type ReactNode, useMemo, useState } from "react";
import { OrderItemCogsBreakdown } from "./OrderItemCogsBreakdown";

type OrderDetailItemGroup = {
  productName: string;
  items: OrderItemDTO[];
};

function groupOrderDetailItems(items: OrderItemDTO[]): OrderDetailItemGroup[] {
  const groups: OrderDetailItemGroup[] = [];
  const indexByKey = new Map<string, number>();

  for (const item of items) {
    const key = item.productName;
    let idx = indexByKey.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByKey.set(key, idx);
      groups.push({ productName: item.productName, items: [] });
    }
    groups[idx]?.items.push(item);
  }

  return groups;
}

function itemHasCogs(item: OrderItemDTO, showCogs: boolean): boolean {
  if (!showCogs || item.isBonus) return false;
  const allocations = item.cogsAllocations ?? [];
  const hasMissing = (item.missingLotQuantity ?? 0) > 0;
  return allocations.length > 0 || hasMissing;
}

function ItemLotCollapsible({
  item,
  showDraftLots,
}: {
  item: OrderItemDTO;
  showDraftLots: boolean;
}) {
  const [open, setOpen] = useState(false);
  const lotPicks = item.lotPicks ?? [];
  if (!showDraftLots || lotPicks.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-1.5">
      <CollapsibleTrigger
        type="button"
        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
      >
        <ChevronDownIcon
          className={cn(
            "size-3.5 transition-transform",
            open && "rotate-180",
          )}
        />
        Lot ທີ່ເລືອກ ({lotPicks.length})
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-1.5 flex flex-col gap-1 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs">
          {lotPicks.map((pick) => (
            <li key={pick.stockLotId} className="tabular-nums">
              {pick.receiptNumber} × {pick.quantity}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** ลูกศรอยู่หน้าสุดของแถว — กดเปิด COGS */
function ItemCogsShell({
  item,
  showCogs,
  children,
}: {
  item: OrderItemDTO;
  showCogs: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const canShow = itemHasCogs(item, showCogs);

  if (!canShow) {
    return <>{children}</>;
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-start gap-1.5">
        <CollapsibleTrigger
          type="button"
          aria-label="FIFO / COGS"
          className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
        >
          <ChevronDownIcon
            className={cn(
              "size-3.5 transition-transform",
              open && "rotate-180",
            )}
          />
        </CollapsibleTrigger>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
      <CollapsibleContent className="mt-1.5 pl-5">
        <OrderItemCogsBreakdown item={item} hideTitle />
      </CollapsibleContent>
    </Collapsible>
  );
}

function ItemBadges({ item }: { item: OrderItemDTO }) {
  return (
    <>
      {item.lineType === "ad_hoc_kit" ? (
        <Badge
          variant="outline"
          className="rounded-full border-amber-600/30 bg-amber-500/10 text-[10px] text-amber-900"
        >
          ຊຸດກຳນົດເອງ
        </Badge>
      ) : null}
      {item.isBonus ? (
        <Badge variant="secondary" className="rounded-full text-[10px]">
          ແຖມ
        </Badge>
      ) : null}
    </>
  );
}

function KitComponentsList({ item }: { item: OrderItemDTO }) {
  if (
    item.lineType !== "ad_hoc_kit" ||
    !item.kitComponents ||
    item.kitComponents.length === 0
  ) {
    return null;
  }

  return (
    <ul className="mt-1.5 flex flex-col gap-0.5 text-[11px] text-muted-foreground">
      {item.kitComponents.map((comp) => (
        <li key={comp.componentVariantId}>
          {comp.productName} · {comp.variantName} ×
          {comp.quantity * item.quantity}
        </li>
      ))}
    </ul>
  );
}

function OrderDetailLineRow({
  item,
  showCogs,
  showDraftLots,
  indented,
}: {
  item: OrderItemDTO;
  showCogs: boolean;
  showDraftLots: boolean;
  indented?: boolean;
}) {
  return (
    <div className={cn(indented && "border-secondary/15 border-l pl-2.5")}>
      <ItemCogsShell item={item} showCogs={showCogs}>
        <div className="flex items-start justify-between gap-3 text-sm">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-muted-foreground leading-snug">
                {item.variantName}
              </span>
              <ItemBadges item={item} />
            </div>
            <p className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
              {item.isBonus
                ? `ຟຣີ × ${item.quantity}`
                : `${formatLak(item.unitPrice)} × ${item.quantity}`}
            </p>
            <KitComponentsList item={item} />
            <ItemLotCollapsible item={item} showDraftLots={showDraftLots} />
          </div>
          <span className="shrink-0 font-semibold tabular-nums">
            {item.isBonus ? "ຟຣີ" : formatLak(item.lineTotal)}
          </span>
        </div>
      </ItemCogsShell>
    </div>
  );
}

type OrderDetailItemsPanelProps = {
  items: OrderItemDTO[];
  showCogs: boolean;
  showDraftLots: boolean;
};

export function OrderDetailItemsPanel({
  items,
  showCogs,
  showDraftLots,
}: OrderDetailItemsPanelProps) {
  const groups = useMemo(() => groupOrderDetailItems(items), [items]);

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => {
        const single = group.items.length === 1 ? group.items[0] : null;

        if (single) {
          return (
            <div
              key={group.productName}
              className="rounded-xl border border-secondary/15 bg-muted/20 px-3 py-2.5"
            >
              <ItemCogsShell item={single} showCogs={showCogs}>
                <div className="flex items-start justify-between gap-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium leading-snug">
                        {group.productName}
                      </span>
                      <span className="text-muted-foreground">
                        ({single.variantName})
                      </span>
                      <ItemBadges item={single} />
                    </div>
                    <p className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                      {single.isBonus
                        ? `ຟຣີ × ${single.quantity}`
                        : `${formatLak(single.unitPrice)} × ${single.quantity}`}
                    </p>
                    <KitComponentsList item={single} />
                    <ItemLotCollapsible
                      item={single}
                      showDraftLots={showDraftLots}
                    />
                  </div>
                  <span className="shrink-0 font-semibold tabular-nums">
                    {single.isBonus ? "ຟຣີ" : formatLak(single.lineTotal)}
                  </span>
                </div>
              </ItemCogsShell>
            </div>
          );
        }

        return (
          <div
            key={group.productName}
            className="rounded-xl border border-secondary/15 bg-muted/20 px-3 py-2.5"
          >
            <p className="font-medium text-sm leading-snug">
              {group.productName}
            </p>
            <ul className="mt-2 flex flex-col gap-2.5">
              {group.items.map((item) => (
                <li key={item.id}>
                  <OrderDetailLineRow
                    item={item}
                    showCogs={showCogs}
                    showDraftLots={showDraftLots}
                    indented
                  />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
